import React from "react";
import {observer} from "mobx-react";
import {IGameContextViewModel} from "../../../services/game-runner/interfaces/game-context/game-context-view-model.interface";
import {useServices} from "../../../react-hooks/use-services.hook";
import {GameJackpotBarWrapperBox} from "./boxes/wrapper/game-jackpot-bar-wrapper.box";
import {GameJackpotBarSectionContainerBox} from "./boxes/containers/game-jackpot-bar-section-container.box";
import {GameJackpotBarContributionAmountBox} from "./boxes/contribution/contribution-amount/game-jackpot-bar-contribution-amount.box";
import {GameJackpotBarContributionLabelBox} from "./boxes/contribution/contrinution-label/game-jackpot-bar-contribution-label.box";
import {useEffect, useState} from "react";
import {GameJackpotBarToggleSwitchBox} from "./boxes/toggle-switch/game-jackpot-bar-toggle-switch.box";
import {GameJackpotBarToggleSliderBox} from "./boxes/toggle-switch/toggle-slider/game-jackpot-bar-toggle-slider.box";
import {GameJackpotBarToggleSwitchInputBox} from "./boxes/toggle-switch/game-jackpot-bar-toggle-switch-input.box";
import {GameJackpotBarPrizesComponent} from "./game-jackpot-bar-prizes.component";
import {GameJackpotBarPrizesWrapperBox} from "./boxes/wrapper/prizes/game-jackpot-bar-prizes-wrapper.box";
import {GameJackpotBarContributionWrapperBox} from "./boxes/wrapper/contribution/game-jackpot-bar-contribution-wrapper.box";
import {JackpotBetsOptStatusType} from "../../../services/jackpot-bet/jackpot-bets.service.interface";
import {CyClass} from "../../../../common/cy-classes";
import {GameJackpotBarContainerBox} from "./boxes/containers/game-jackpot-bar-container.box";
import {GameJackpotBarLogoBox} from "./boxes/logo/game-jackpot-bar-logo.box";
import {JackpotBarMagicBallComponent} from "../jackpot-bar-magic-ball/jackpot-bar-magic-ball.component";
import {IAvailablePrizes} from "./interfaces/game-jackpot-bar-available-prizes-data";

interface IGameJackpotBarComponentProps {
    gameContext: IGameContextViewModel;
}

export const GameJackpotBarComponent: React.FC<IGameJackpotBarComponentProps> = observer((props) => {
    const services = useServices();
    const [currentJackpotBetIndex, setCurrentJackpotBetIndex] = useState(0);
    const [toggleStatus, setToggleStatus] = useState(JackpotBetsOptStatusType.Unknown);
    const [participationFee, setParticipationFee] = useState(0);
    const isMobilePortrait = services.mediaQuery.sAndBelow && services.mediaQuery.portrait;

    const availablePrizes: IAvailablePrizes[] = services.jackpotBet.jackpotsInfo.getPrizes().map(prize => {
        return {
            amount: services.currency.formatMoney(prize.amount, true),
            label: `${prize?.label}`
        }
    });
    availablePrizes.push({
        amount: services.jackpotBet.jackpotsInfo.winnersCount.toString(),
        label: services.translation.translate('JACKPOT_BET_WINNERS_TODAY')
    });

    useEffect(() => {
        if (availablePrizes.length === 0) {
            return;
        }

        // increasing the availablePrizesCount by 1 to create a placeholder for showing the logo as a prize
        const availablePrizesCount = isMobilePortrait ? availablePrizes.length + 1 : availablePrizes.length;

        let interval = setInterval(() => {
            setCurrentJackpotBetIndex((prevIndex) => (prevIndex + 1) % availablePrizesCount);
        }, 5000); // Change the displayed jackpot every 5 seconds

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        }
    }, [services.jackpotBet, availablePrizes.length, isMobilePortrait]);

    useEffect(() => {
        services.jackpotBet.getParticipationData().then(data => {
            setToggleStatus(data.optInStatus);
            setParticipationFee(data.participationFee);
        });
    }, [services.jackpotBet, services.jackpotBet.optInStatus]);

    const onToggleHandler = async () => {
        if (toggleStatus === JackpotBetsOptStatusType.OptIn) {
            setToggleStatus(JackpotBetsOptStatusType.OptOut);
        } else {
            setToggleStatus(JackpotBetsOptStatusType.OptIn);
        }

        try {
            await services.jackpotBet.toggleJackpotBet();
        } catch (error) {
            services.jackpotBet.sendJackpotBetTechnicalEvent("status_toggle_failed", "src_game_bar", JSON.stringify(error));
        } finally {
            const newOptInStatus = await services.jackpotBet.getOptInStatus();
            setToggleStatus(newOptInStatus);
            services.jackpotBet.sendJackpotBetEvent("jackpot_bar_toggle_button",
                "", `new_optin_status_${newOptInStatus}`);
        }
    }

    if (!services.jackpotBet.isEnabled) {
        return null;
    }
    if (!props.gameContext.isGameReady) {
        return null;
    }

    const currentPrize: IAvailablePrizes | undefined = availablePrizes[currentJackpotBetIndex];
    const isOptInStatus = toggleStatus === JackpotBetsOptStatusType.OptIn;

    return (
        <GameJackpotBarWrapperBox>
            {services.magic8ball.shouldRenderBall ? <JackpotBarMagicBallComponent /> : null}

            <GameJackpotBarContainerBox isOptInStatus={isOptInStatus}>
                {!isMobilePortrait && <GameJackpotBarLogoBox isMobilePortrait={isMobilePortrait} />}

                <GameJackpotBarPrizesWrapperBox>
                    <GameJackpotBarPrizesComponent isOptInStatus={isOptInStatus} currentPrize={currentPrize} isMobilePortrait={isMobilePortrait} />
                </GameJackpotBarPrizesWrapperBox>

                <GameJackpotBarContributionWrapperBox isMobilePortrait={isMobilePortrait}>
                    <GameJackpotBarSectionContainerBox>
                        <GameJackpotBarContributionAmountBox isOptInStatus={isOptInStatus} className={CyClass('cy-jp-bets-game-bar-contribution-fee-amount')}>
                            {services.currency.formatMoney(participationFee, true)}
                        </GameJackpotBarContributionAmountBox>
                        <GameJackpotBarContributionLabelBox isOptInStatus={isOptInStatus} className={CyClass('cy-jp-bets-game-bar-contribution-fee-label')}>
                            {services.translation.translate('JACKPOT_BET_CONTRIBUTION')}
                        </GameJackpotBarContributionLabelBox>
                    </GameJackpotBarSectionContainerBox>

                    <GameJackpotBarToggleSwitchBox className={CyClass('cy-jackpot-game-bar-toggle')}>
                        <GameJackpotBarToggleSwitchInputBox checked={isOptInStatus} onChange={onToggleHandler} />
                        <GameJackpotBarToggleSliderBox checked={isOptInStatus} className={CyClass('cy-toggle-thumb')} />
                    </GameJackpotBarToggleSwitchBox>
                </GameJackpotBarContributionWrapperBox>
            </GameJackpotBarContainerBox>
        </GameJackpotBarWrapperBox>
    )
});